export const DOC_CONTEXT_CLIPBOARD_MIME = 'application/x-cg-doc-context'

const DRAFT_CHAT_KEY = '__draft__'
const PINS_STORAGE_KEY = 'workbench-doc-pins'

let lastCopiedDocumentContext = null
let sendTimeDocumentContext = null
// Chips are scoped per chat: { [chatKey]: context[] }.
let pinsByChat = {}
let pinsWorkbenchId = null
let activeChatKey = DRAFT_CHAT_KEY
// Where the user is in the embedded docs editor (for placement defaults).
// sectionId follows the URL immediately; tabId/groupId come from GraphQL once
// the loaded section matches that route.
let currentDocumentLocation = null
const listeners = new Set()

const chatKey = chatId => (chatId ? String(chatId) : DRAFT_CHAT_KEY)

const activePins = () => pinsByChat[activeChatKey] || []

const loadPinsForWorkbench = workbenchId => {
  if (!workbenchId) {
    pinsByChat = {}
    return
  }

  try {
    const stored = JSON.parse(localStorage.getItem(PINS_STORAGE_KEY) || '{}')
    const loaded = stored[String(workbenchId)]
    pinsByChat = loaded && typeof loaded === 'object' ? loaded : {}
  } catch {
    pinsByChat = {}
  }
}

const persistPins = () => {
  if (!pinsWorkbenchId) return

  try {
    const stored = JSON.parse(localStorage.getItem(PINS_STORAGE_KEY) || '{}')
    const id = String(pinsWorkbenchId)

    if (Object.keys(pinsByChat).length > 0) {
      stored[id] = pinsByChat
    } else {
      delete stored[id]
    }

    localStorage.setItem(PINS_STORAGE_KEY, JSON.stringify(stored))
  } catch {
    // Ignore quota / private-mode storage errors.
  }
}

/** Load/save pins for the active workbench — chips survive page refresh. */
export const setDocumentChatPinsWorkbench = workbenchId => {
  const next = workbenchId ? String(workbenchId) : null
  if (next === pinsWorkbenchId) return

  pinsWorkbenchId = next
  loadPinsForWorkbench(next)
  notify()
}

const setActivePins = pins => {
  if (pins.length) {
    pinsByChat = { ...pinsByChat, [activeChatKey]: pins }
  } else {
    const { [activeChatKey]: _removed, ...rest } = pinsByChat
    pinsByChat = rest
  }
  persistPins()
}

/** Stable identity for a pinned span — survives text edits within the same block. */
export const getDocumentPinKey = context => {
  if (!context) return ''
  return [
    context.sectionId,
    context.blockIndex,
    context.blockIndexEnd ?? context.blockIndex,
    context.isExpanded ? 1 : 0,
    context.selectionStart ?? 0
  ].join(':')
}

const notify = () => {
  const pins = activePins()
  listeners.forEach(listener => listener(pins))
}

export const subscribeDocumentChatContext = listener => {
  listeners.add(listener)
  listener(activePins())
  return () => listeners.delete(listener)
}

/**
 * Point the store at the chat the user is currently viewing. Chips are
 * per-conversation, so switching chats swaps which pins are visible. A brand-new
 * chat starts under DRAFT_CHAT_KEY; when it gets a real id, draft pins migrate so
 * they survive the first send.
 */
export const setActiveDocumentChat = chatId => {
  const nextKey = chatKey(chatId)
  if (nextKey === activeChatKey) return

  const draftPins = pinsByChat[DRAFT_CHAT_KEY] || []
  if (
    activeChatKey === DRAFT_CHAT_KEY &&
    nextKey !== DRAFT_CHAT_KEY &&
    draftPins.length &&
    !(pinsByChat[nextKey]?.length)
  ) {
    const { [DRAFT_CHAT_KEY]: _draft, ...rest } = pinsByChat
    pinsByChat = { ...rest, [nextKey]: draftPins }
  }

  activeChatKey = nextKey
  persistPins()
  notify()
}

/** Called from the doc editor on copy — this is what chat paste attaches. */
export const setCopiedDocumentContext = context => {
  lastCopiedDocumentContext = context || null
}

export const getCopiedDocumentContext = () => lastCopiedDocumentContext

/**
 * Tracks where the user is in the embedded docs editor.
 * @param {{ sectionId?: string|null, tabId?: string|null, groupId?: string|null }|null} location
 */
export const setCurrentDocumentLocation = location => {
  if (!location?.sectionId) {
    currentDocumentLocation = null
    return
  }

  currentDocumentLocation = {
    sectionId: String(location.sectionId),
    tabId: location.tabId ? String(location.tabId) : null,
    groupId: location.groupId ? String(location.groupId) : null
  }
}

export const getCurrentDocumentLocation = () => currentDocumentLocation

/** @deprecated use getCurrentDocumentLocation */
export const getCurrentDocumentSection = () => currentDocumentLocation?.sectionId || null

export const writeDocumentContextToClipboard = (clipboardData, context) => {
  if (!clipboardData || !context?.selectionText) return

  try {
    clipboardData.setData('text/plain', context.selectionText)
    clipboardData.setData(DOC_CONTEXT_CLIPBOARD_MIME, JSON.stringify(context))
  } catch {
    // Clipboard may be unavailable in some browsers/contexts.
  }
}

export const readDocumentContextFromClipboard = clipboardData => {
  if (!clipboardData) return null

  try {
    const raw = clipboardData.getData(DOC_CONTEXT_CLIPBOARD_MIME)
    if (!raw) return null

    const parsed = JSON.parse(raw)
    return parsed?.selectionText ? parsed : null
  } catch {
    return null
  }
}

/** Set document context for the next outgoing message (inline composer). */
export const setSendTimeDocumentContext = context => {
  sendTimeDocumentContext = context || null
}

/** Context included on chat send — composer snapshot first, then legacy pins. */
export const getDocumentChatContextForSend = () => {
  if (sendTimeDocumentContext) {
    const ctx = sendTimeDocumentContext
    sendTimeDocumentContext = null
    return ctx
  }

  const pins = activePins()
  if (!pins.length) return null

  const firstContext = pins[0]
  return {
    documentId: firstContext.documentId,
    versionId: firstContext.versionId,
    workspaceId: firstContext.workspaceId,
    references: pins
  }
}

export const getVisibleDocumentChatContext = () => activePins()

export const dismissDocumentChatContext = context => {
  if (!context) return
  const key = getDocumentPinKey(context)
  setActivePins(activePins().filter(item => getDocumentPinKey(item) !== key))
  lastCopiedDocumentContext = null
  notify()
}

export const unpinDocumentChatContext = () => {
  setActivePins([])
  notify()
}

export const resetDocumentChatAttachment = () => {
  lastCopiedDocumentContext = null
  setActivePins([])
  notify()
}

export const clearDocumentEditorSession = () => {
  lastCopiedDocumentContext = null
  currentDocumentLocation = null
}

export const clearDocumentChatContext = () => {
  lastCopiedDocumentContext = null
  pinsByChat = {}
  currentDocumentLocation = null
  persistPins()
  notify()
}

/** Attach a copied doc selection to the active chat (paste into chat input). */
export const pinDocumentChatContext = context => {
  if (!context) return
  const key = getDocumentPinKey(context)
  const pins = activePins()
  const existingIndex = pins.findIndex(item => getDocumentPinKey(item) === key)

  if (existingIndex >= 0) {
    setActivePins(pins.map((item, index) => (index === existingIndex ? context : item)))
  } else {
    // One chip at a time — a new paste replaces any previous attachment.
    setActivePins([context])
  }

  notify()
}
